import { BELL_Z_RANGE, color, font, testColors, type TestTone } from "@mesketch/design-tokens";

// Grafy Skici s portrétem (CR-015 §4, zadání 27.7): zvon pro každou škálu a květ jako přehled.
// Bez čísel (CON-002), texty a textový ekvivalent dodá volající. Hodnoty z payloadu (`z`, `ci`)
// a z čistých funkcí `libs/portrait` (`flowerPosition`), komponenty nic nepočítají navíc.

const r1 = (v: number) => Math.round(v * 10) / 10;
const clampZ = (z: number) => Math.max(-BELL_Z_RANGE, Math.min(BELL_Z_RANGE, z));

const BELL = { w: 358, h: 92, left: 8, right: 350, base: 70, peak: 60 } as const;
const bellX = (z: number) => BELL.left + ((clampZ(z) + BELL_Z_RANGE) / (2 * BELL_Z_RANGE)) * (BELL.right - BELL.left);
const bellY = (z: number) => BELL.base - BELL.peak * Math.exp((-clampZ(z) * clampZ(z)) / 2);

/** Plocha pod křivkou mezi `from` a `to` (z), uzavřená k ose. */
function area(from: number, to: number): string {
  const a = clampZ(from);
  const b = clampZ(to);
  if (b <= a) return "";
  const steps = Math.max(2, Math.ceil(((b - a) / (2 * BELL_Z_RANGE)) * 80));
  const pts: string[] = [];
  for (let i = 0; i <= steps; i++) {
    const z = a + ((b - a) * i) / steps;
    pts.push(`L${r1(bellX(z))} ${r1(bellY(z))}`);
  }
  return `M${r1(bellX(a))} ${BELL.base}${pts.join("")}L${r1(bellX(b))} ${BELL.base}Z`;
}

function curve(): string {
  const pts: string[] = [];
  for (let i = 0; i <= 120; i++) {
    const z = -BELL_Z_RANGE + (2 * BELL_Z_RANGE * i) / 120;
    pts.push(`${i === 0 ? "M" : "L"}${r1(bellX(z))} ${r1(bellY(z))}`);
  }
  return pts.join("");
}

export interface BellProps {
  readonly z: number;
  /** Pásmo nejistoty z payloadu, `null` bez nejistoty. */
  readonly ci: readonly [number, number] | null;
  /** Neutrální pásmo definice (DES-003: střed se kreslí z definice), `null` bez něj. */
  readonly neutral: readonly [number | null, number | null] | null;
  /** Barva prvku obrazu (`elementColor`). */
  readonly color: string;
  readonly lowReliability: boolean;
  /** „většina lidí“ pod středem. */
  readonly mostLabel: string;
  /** Textový ekvivalent (`aria-label`). */
  readonly label: string;
}

/** Zvon: hustota normálního rozdělení, tmavší střed, pásmo nejistoty, tečka nad křivkou. */
export function Bell({ z, ci, neutral, color: element, lowReliability, mostLabel, label }: BellProps) {
  const x = r1(bellX(z));
  const y = r1(bellY(z) - 8);
  const mid = neutral === null ? "" : area(neutral[0] ?? -BELL_Z_RANGE, neutral[1] ?? BELL_Z_RANGE);
  const band = ci === null ? "" : area(ci[0], ci[1]);
  return (
    <svg viewBox={`0 0 ${BELL.w} ${BELL.h}`} width="100%" role="img" aria-label={label} data-testid="bell" style={{ display: "block" }}>
      <path d={area(-BELL_Z_RANGE, BELL_Z_RANGE)} fill={color.bellFill} />
      {mid === "" ? null : <path d={mid} fill={color.bellMid} />}
      {band === "" ? null : (
        <path
          d={band}
          fill={element}
          fillOpacity={lowReliability ? 0.22 : 0.32}
          stroke={lowReliability ? element : "none"}
          strokeWidth={lowReliability ? 1.4 : 0}
          strokeDasharray={lowReliability ? "4 3" : undefined}
          data-low={lowReliability ? "true" : undefined}
        />
      )}
      <path d={curve()} fill="none" stroke={color.chartAxis} strokeWidth="1.4" />
      <line x1={BELL.left} y1={BELL.base} x2={BELL.right} y2={BELL.base} stroke={color.chartAxis} strokeWidth="1.2" />
      <line x1={x} y1={y} x2={x} y2={BELL.base} stroke={color.ink} strokeWidth="2" />
      <circle cx={x} cy={y} r="7" fill={color.ink} stroke={color.card} strokeWidth="3" paintOrder="stroke" data-testid="bell-dot" />
      <text x={(BELL.left + BELL.right) / 2} y="86" textAnchor="middle" fontSize="11" fill={color.mutedStrong} fontFamily={font.text}>
        {mostLabel}
      </text>
    </svg>
  );
}

export interface Petal {
  readonly id: string;
  /** Krátký název škály. */
  readonly name: string;
  /** Slovo pólu u výrazné škály, jinak `null`. */
  readonly pole: string | null;
  /** Pozice 0 až 100 (`flowerPosition`), `null` u nespočítané škály (lístek chybí). */
  readonly position: number | null;
  readonly prominent: boolean;
  readonly lowReliability: boolean;
}

export interface FlowerProps {
  readonly petals: readonly Petal[];
  readonly tone: TestTone;
  readonly label: string;
}

const FLOWER = { size: 360, c: 180, labelR: 148 } as const;
const petalLength = (position: number) => 26 + position * 0.92;

/** Květ: lístek pro každou doménu dokola, první nahoře, délka podle pozice, kruh středu čárkovaně. */
export function Flower({ petals, tone, label }: FlowerProps) {
  const { c } = FLOWER;
  const n = petals.length;
  const angle = (i: number) => -Math.PI / 2 + (2 * Math.PI * i) / n;
  return (
    <svg viewBox={`0 0 ${FLOWER.size} ${FLOWER.size}`} width="100%" role="img" aria-label={label} data-testid="flower" style={{ display: "block" }}>
      <circle cx={c} cy={c} r={petalLength(100)} fill="none" stroke={color.line} strokeWidth="1.2" />
      <circle cx={c} cy={c} r={petalLength(50)} fill="none" stroke={color.chartAxis} strokeWidth="1.2" strokeDasharray="4 5" />
      {petals.map((p, i) => {
        if (p.position === null) return null;
        const a = angle(i);
        const L = petalLength(p.position);
        const ux = Math.cos(a);
        const uy = Math.sin(a);
        const tip = [c + ux * L, c + uy * L] as const;
        const along = 0.59 * L;
        const side = 0.184 * L;
        const q1 = [c + ux * along - uy * side, c + uy * along + ux * side] as const;
        const q2 = [c + ux * along + uy * side, c + uy * along - ux * side] as const;
        const d = `M${c} ${c} Q${r1(q1[0])} ${r1(q1[1])} ${r1(tip[0])} ${r1(tip[1])} Q${r1(q2[0])} ${r1(q2[1])} ${c} ${c} Z`;
        return (
          <path
            key={p.id}
            d={d}
            data-petal={p.id}
            data-prominent={p.prominent ? "true" : undefined}
            fill={p.prominent ? testColors[tone].main : color.petalLight}
            stroke={p.lowReliability ? color.taupe : "none"}
            strokeWidth={p.lowReliability ? 1.6 : 0}
            strokeDasharray={p.lowReliability ? "4 4" : undefined}
          />
        );
      })}
      <circle cx={c} cy={c} r="13" fill={color.ink} />
      <circle cx={c} cy={c} r="5" fill={color.paper} />
      {petals.map((p, i) => {
        const a = angle(i);
        const x = Math.max(52, Math.min(FLOWER.size - 52, c + Math.cos(a) * FLOWER.labelR));
        const y = c + Math.sin(a) * FLOWER.labelR;
        const muted = p.position === null;
        return (
          <text key={p.id} x={r1(x)} y={r1(p.pole === null ? y + 4 : y - 3)} textAnchor="middle" fontSize="12" fontFamily={font.text}>
            <tspan fontWeight={p.prominent ? 700 : 400} fill={p.prominent ? testColors[tone].ink : muted ? color.taupe : color.ink}>
              {p.name}
            </tspan>
            {p.pole === null ? null : (
              <tspan x={r1(x)} dy="15" fill={color.mutedStrong}>
                {p.pole}
              </tspan>
            )}
          </text>
        );
      })}
    </svg>
  );
}

export interface PairBellProps {
  /** `z` čtenáře a druhé strany, `null` u nespočítané škály (tečka chybí). */
  readonly me: number | null;
  readonly other: number | null;
  readonly neutral: readonly [number | null, number | null] | null;
  /** Barva tečky druhé strany (tečka čtenáře je inkoustová). */
  readonly otherColor: string;
  readonly mostLabel: string;
  /** Textový ekvivalent (`aria-label`): kdo je víc vlevo a kdo vpravo. */
  readonly label: string;
}

/** Zvon páru (REQ-035): stejná křivka jako Skica, dvě tečky odlišené barvou, bez čísel. */
export function PairBell({ me, other, neutral, otherColor, mostLabel, label }: PairBellProps) {
  const mid = neutral === null ? "" : area(neutral[0] ?? -BELL_Z_RANGE, neutral[1] ?? BELL_Z_RANGE);
  const dot = (z: number, fill: string, testId: string, ring: boolean) => {
    const x = r1(bellX(z));
    const y = r1(bellY(z) - 8);
    return (
      <g key={testId}>
        <line x1={x} y1={y} x2={x} y2={BELL.base} stroke={fill} strokeWidth="2" />
        <circle cx={x} cy={y} r="7" fill={ring ? color.card : fill} stroke={ring ? fill : color.card} strokeWidth="3" paintOrder="stroke" data-testid={testId} />
      </g>
    );
  };
  return (
    <svg viewBox={`0 0 ${BELL.w} ${BELL.h}`} width="100%" role="img" aria-label={label} data-testid="pair-bell" style={{ display: "block" }}>
      <path d={area(-BELL_Z_RANGE, BELL_Z_RANGE)} fill={color.bellFill} />
      {mid === "" ? null : <path d={mid} fill={color.bellMid} />}
      <path d={curve()} fill="none" stroke={color.chartAxis} strokeWidth="1.4" />
      <line x1={BELL.left} y1={BELL.base} x2={BELL.right} y2={BELL.base} stroke={color.chartAxis} strokeWidth="1.2" />
      {other === null ? null : dot(other, otherColor, "bell-dot-other", true)}
      {me === null ? null : dot(me, color.ink, "bell-dot-me", false)}
      <text x={(BELL.left + BELL.right) / 2} y="86" textAnchor="middle" fontSize="11" fill={color.mutedStrong} fontFamily={font.text}>
        {mostLabel}
      </text>
    </svg>
  );
}

export interface PairFlowerProps {
  /** Lístky čtenáře (názvy, pozice); `other` jsou pozice druhé strany ve stejném pořadí. */
  readonly petals: readonly Petal[];
  readonly other: readonly (number | null)[];
  readonly otherColor: string;
  readonly label: string;
}

function petalPath(i: number, n: number, position: number): string {
  const { c } = FLOWER;
  const a = -Math.PI / 2 + (2 * Math.PI * i) / n;
  const L = petalLength(position);
  const ux = Math.cos(a);
  const uy = Math.sin(a);
  const along = 0.59 * L;
  const side = 0.184 * L;
  return `M${c} ${c} Q${r1(c + ux * along - uy * side)} ${r1(c + uy * along + ux * side)} ${r1(c + ux * L)} ${r1(c + uy * L)} Q${r1(c + ux * along + uy * side)} ${r1(c + uy * along - ux * side)} ${c} ${c} Z`;
}

/** Květ páru (REQ-035): dva obrysy lístků přes sebe, čtenář inkoustem, druhá strana barvou. */
export function PairFlower({ petals, other, otherColor, label }: PairFlowerProps) {
  const { c } = FLOWER;
  const n = petals.length;
  return (
    <svg viewBox={`0 0 ${FLOWER.size} ${FLOWER.size}`} width="100%" role="img" aria-label={label} data-testid="pair-flower" style={{ display: "block" }}>
      <circle cx={c} cy={c} r={petalLength(100)} fill="none" stroke={color.line} strokeWidth="1.2" />
      <circle cx={c} cy={c} r={petalLength(50)} fill="none" stroke={color.chartAxis} strokeWidth="1.2" strokeDasharray="4 5" />
      {other.map((position, i) =>
        position === null ? null : <path key={`o${i}`} d={petalPath(i, n, position)} fill={otherColor} fillOpacity="0.18" stroke={otherColor} strokeWidth="2.2" data-layer="other" />,
      )}
      {petals.map((p, i) =>
        p.position === null ? null : <path key={p.id} d={petalPath(i, n, p.position)} fill="none" stroke={color.ink} strokeWidth="2" strokeDasharray="5 4" data-layer="me" />,
      )}
      <circle cx={c} cy={c} r="13" fill={color.ink} />
      <circle cx={c} cy={c} r="5" fill={color.paper} />
      {petals.map((p, i) => {
        const a = -Math.PI / 2 + (2 * Math.PI * i) / n;
        const x = Math.max(52, Math.min(FLOWER.size - 52, c + Math.cos(a) * FLOWER.labelR));
        const y = c + Math.sin(a) * FLOWER.labelR;
        return (
          <text key={p.id} x={r1(x)} y={r1(y + 4)} textAnchor="middle" fontSize="12" fontFamily={font.text} fill={color.ink}>
            {p.name}
          </text>
        );
      })}
    </svg>
  );
}
